import { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { TagInput } from '@/components/crm/TagInput';
import { router } from '@/router';
import { ApiError } from '@/lib/api';
import {
  createCompany,
  createContact,
} from '@/lib/crm/api';

const INN_RE = /^(\d{10}|\d{12})$/;

/**
 * /crm/new — создание клиента (screen 4).
 * Форма Company (name обяз.) + опц. контакт.
 *
 * Переключателя «завести в систему сразу» здесь больше нет: рабочий кабинет
 * клиента создаётся ЛЕНИВО, при добавлении первого поля (модель D). Пока
 * полей нет, клиент — запись CRM, и заводить под него схему не за что.
 */
export function CrmNewPage() {
  const [name, setName] = useState('');
  const [inn, setInn] = useState('');
  const [ogrn, setOgrn] = useState('');
  const [address, setAddress] = useState('');
  const [director, setDirector] = useState('');
  const [tags, setTags] = useState<string[]>([]);

  const [contactName, setContactName] = useState('');
  const [contactPosition, setContactPosition] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Название обязательно (AC-1.1).');
      return;
    }
    if (inn.trim() && !INN_RE.test(inn.trim())) {
      setError('ИНН должен содержать 10 или 12 цифр (AC-1.2).');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const company = await createCompany({
        name: name.trim(),
        inn: inn.trim() || null,
        ogrn: ogrn.trim() || null,
        address: address.trim() || null,
        director_name: director.trim() || null,
        tags,
      });

      // опциональный контакт
      if (contactName.trim()) {
        await createContact({
          company_id: company.id,
          full_name: contactName.trim(),
          position: contactPosition.trim() || null,
          phone: contactPhone.trim() || null,
          email: contactEmail.trim() || null,
          is_primary: true,
        });
      }

      router.navigate({
        to: '/crm/$companyId',
        params: { companyId: company.id },
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : 'Не удалось сохранить клиента');
      setSubmitting(false);
    }
  };

  return (
    <AppShell>
      <div className="mx-auto w-full max-w-5xl space-y-5 p-6 pb-fab">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => router.navigate({ to: '/crm' })}
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> Клиенты
          </Button>
          <h1 className="text-2xl font-semibold">Новый клиент</h1>
        </div>

        <form onSubmit={handleSubmit} data-testid="new-client-form">
          <div className="grid gap-4 md:grid-cols-[620px_1fr]">
            <Card className="space-y-5 p-5">
              <fieldset className="space-y-4">
                <legend className="text-sm font-semibold">
                  Реквизиты хозяйства
                </legend>
                <div className="space-y-1">
                  <Label htmlFor="co-name">
                    Название <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="co-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="ООО «…» / КФХ / ИП"
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Обязательно. Без названия форма не сохранится.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="co-inn">ИНН — опц.</Label>
                    <Input
                      id="co-inn"
                      value={inn}
                      onChange={(e) => setInn(e.target.value)}
                      placeholder="10 или 12 цифр"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="co-ogrn">ОГРН — опц.</Label>
                    <Input
                      id="co-ogrn"
                      value={ogrn}
                      onChange={(e) => setOgrn(e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="co-address">Адрес — опц.</Label>
                  <Input
                    id="co-address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="регион, населённый пункт"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="co-director">Директор — опц.</Label>
                  <Input
                    id="co-director"
                    value={director}
                    onChange={(e) => setDirector(e.target.value)}
                    placeholder="ФИО"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="co-tags">Теги — опц., свободный ввод</Label>
                  <TagInput id="co-tags" value={tags} onChange={setTags} />
                </div>
              </fieldset>

              <Separator />

              <fieldset className="space-y-4">
                <legend className="text-sm font-semibold">
                  Контактное лицо{' '}
                  <span className="font-normal text-muted-foreground">
                    — опц., можно добавить позже
                  </span>
                </legend>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="ct-name">ФИО</Label>
                    <Input
                      id="ct-name"
                      value={contactName}
                      onChange={(e) => setContactName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="ct-position">Должность</Label>
                    <Input
                      id="ct-position"
                      value={contactPosition}
                      onChange={(e) => setContactPosition(e.target.value)}
                      placeholder="директор / агроном"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="ct-phone">Телефон</Label>
                    <Input
                      id="ct-phone"
                      value={contactPhone}
                      onChange={(e) => setContactPhone(e.target.value)}
                      placeholder="+7 …"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="ct-email">Email</Label>
                    <Input
                      id="ct-email"
                      type="email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                    />
                  </div>
                </div>
              </fieldset>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="flex items-center justify-between">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => router.navigate({ to: '/crm' })}
                >
                  Отмена
                </Button>
                <Button type="submit" disabled={submitting} data-testid="save-client">
                  {submitting ? 'Сохраняем…' : 'Сохранить клиента'}
                </Button>
              </div>
            </Card>

            <Card className="space-y-2 p-5">
              <p className="text-sm font-semibold">Что произойдёт</p>
              <ul className="list-disc space-y-1.5 pl-5 text-sm text-muted-foreground">
                <li>Создастся карточка клиента в вашем журнале.</li>
                <li>
                  Если переключатель «завести в систему» включён — создастся окно
                  клиента, и кнопка «Войти к клиенту» станет активной.
                </li>
                <li>
                  Если выключен — клиент сохранится как <b>лид</b>; осмотр полей
                  будет недоступен, пока не заведёте его в систему.
                </li>
              </ul>
            </Card>
          </div>
        </form>
      </div>
    </AppShell>
  );
}
